import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Tooltip,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';

import { useNavigate } from 'react-router';
import Authorization from '../../../utils/Authorization';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { selectedDayFilterList, MenuProps, fetchSchedulerPerformanceHub } from '../WidgetService';
import { WidgetTimeFilterEnum } from '../../../Enum/WidgetTimeFilterEnum';
import SchedulerSummaryModel from '../../../Models/Widget/SchedulerSummary.model';
import '../Widgets.css';

const filterList = selectedDayFilterList();

const SchedulerPerformanceHub = () => {
    const schedulingRole = 'Scheduling';
    const history = useNavigate();
    const authData = new Authorization();
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [metrics, setMetrics] = useState<SchedulerSummaryModel>();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const {
        schedulerSelectedProvider,
        setSchedulerSelectedProvider,
        schedulerDateFilter,
        setSchedulerDateFilter,
        schedulerFromDate,
        setSchedulerFromDate,
        schedulerToDate,
        setSchedulerToDate,
        schedulerShowInActiveProviders,
        setSchedulerShowInActiveProviders,
    } = useWidgetContext();
    const [isDataShow, setIsDataShow] = useState<boolean>(false);
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const filteredProviders = (schedulerShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const schedulingProviders = filteredProviders.filter((p) => p.Role.RoleName === schedulingRole);

    const handleClick = (event) => {
        setShow(!show);
    };

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (schedulingProviders.length && !schedulerSelectedProvider) {
            const loggedInProviderId = authData.UserId;
            if (Boolean(loggedInProviderId)) {
                const providerInfo = schedulingProviders.find((t) => t.Id === loggedInProviderId);
                if (Boolean(providerInfo)) {
                    setSchedulerSelectedProvider(loggedInProviderId);
                } else {
                    setSchedulerSelectedProvider(schedulingProviders[0].Id);
                }
            }
        }
    }, [schedulingProviders, authData.UserId, schedulerSelectedProvider, setSchedulerSelectedProvider]);

    useEffect(() => {
        if (schedulerSelectedProvider && (schedulerDateFilter !== WidgetTimeFilterEnum.Custom || (Boolean(schedulerFromDate) && Boolean(schedulerToDate)))) {
            setIsDataShow(false);
            const changeFilter = async () => {
                const data = await fetchSchedulerPerformanceHub(
                    schedulerSelectedProvider,
                    schedulerDateFilter,
                    Boolean(schedulerFromDate) && schedulerDateFilter === WidgetTimeFilterEnum.Custom ? new Date(schedulerFromDate).toISOString() : '',
                    Boolean(schedulerToDate) && schedulerDateFilter === WidgetTimeFilterEnum.Custom ? new Date(schedulerToDate).toISOString() : '',
                    false
                );
                setMetrics(data);
                setIsDataShow(true);
            };
            changeFilter();
        }

        if (schedulerDateFilter !== WidgetTimeFilterEnum.Custom && Boolean(schedulerFromDate) && Boolean(schedulerToDate)) {
            setSchedulerFromDate(null);
            setSchedulerToDate(null);
        }
    }, [schedulerSelectedProvider, schedulerDateFilter, schedulerFromDate, schedulerToDate, setSchedulerFromDate, setSchedulerToDate]);

    const changeFromDate = (date: any) => {
        setSchedulerFromDate(date);
    };

    const changeToDate = (date: any) => {
        setSchedulerToDate(date);
    };

    const toggleInactiveHandler = (isChecked: boolean) => {
        setSchedulerShowInActiveProviders(isChecked);
        setSchedulerSelectedProvider('');
    };

    const navigateToMembers = () => {
        history(`/provider/metricsummary/schedulerperformancehubdetail`);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'} ref={ref}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Scheduler Performance Hub
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                1 -
                                            </Typography>
                                            <Typography variant="caption">Schedule initial appointments within 48 hours post onboard date</Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                2 -
                                            </Typography>
                                            <Typography variant="caption">Average days to schedule a member post successful onboard appointment</Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                3 -
                                            </Typography>
                                            <Typography variant="caption">Confirmation rate for 3 business days out</Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                4 -
                                            </Typography>
                                            <Typography variant="caption">
                                                NCNS/cancellation appointments rescheduled within 7 days of the NCNS or cancelled appointment
                                            </Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                5-
                                            </Typography>
                                            <Typography variant="caption">
                                                NCNS/cancellation appointments rescheduled within 14 days of the NCNS or cancelled appointment
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        <Grid size={3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Schedulers</InputLabel>
                                <Select
                                    value={schedulerSelectedProvider}
                                    label="Schedulers"
                                    name="Schedulers"
                                    onChange={(e) => setSchedulerSelectedProvider(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={
                                            <Checkbox checked={schedulerShowInActiveProviders} onChange={(e) => toggleInactiveHandler(e.target.checked)} />
                                        }
                                        label="Show Inactive"
                                    />
                                    {schedulingProviders.map((pro) => (
                                        <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {pro.LastName}, {pro.FirstName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={schedulerDateFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setSchedulerDateFilter(parseInt(e.target.value as any))}
                                >
                                    {filterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {schedulerDateFilter === WidgetTimeFilterEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 3 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(schedulerFromDate) ? schedulerFromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 3 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(schedulerToDate) ? schedulerToDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : null}
                        <Grid size={12} sx={{ position: 'relative', height: 'calc(100vh - 285px)', maxHeight: '500px' }}>
                            {!isDataShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table aria-label="simple table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Metric</TableCell>
                                                <TableCell align="right">Result</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow key={1}>
                                                <TableCell>% of scheduled within 48 hours of onboarding</TableCell>
                                                <TableCell align="right" style={{ paddingLeft: 0, paddingRight: 10, fontSize: 15 }}>
                                                    <b>{metrics.ScheduledWithin48HoursOfOnboarding}%</b>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow key={3}>
                                                <TableCell>Average days to first appointment after onboarding</TableCell>
                                                <TableCell align="right" style={{ paddingLeft: 0, paddingRight: 10, fontSize: 15 }}>
                                                    <b> {metrics.AverageDaysToFirstAppointmentAfterOnboarding} Days</b>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow key={4}>
                                                <TableCell>% of confirmations completed within 3 days</TableCell>
                                                <TableCell align="right" style={{ paddingLeft: 0, paddingRight: 10, fontSize: 15 }}>
                                                    <b>{metrics.ConfirmedWithinThreeDays}%</b>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow key={5}>
                                                <TableCell>% of cancels/NCNSs rescheduled within 7 days of original appointments</TableCell>
                                                <TableCell align="right" style={{ paddingLeft: 0, paddingRight: 10, fontSize: 15 }}>
                                                    <b>{metrics.RescheduledWithin7Days}%</b>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow key={6}>
                                                <TableCell>% of cancels/NCNSs rescheduled within 14 days of original appointments</TableCell>
                                                <TableCell align="right" style={{ paddingLeft: 0, paddingRight: 10, fontSize: 15 }}>
                                                    <b>{metrics.RescheduledWithin14Days}%</b>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default SchedulerPerformanceHub;
